import { ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { DayPicker, getDefaultClassNames } from 'react-day-picker';

import { buttonVariants } from '@/components/ui/button';

import { cn } from '@/lib/utils';

import type { ComponentProps } from 'react';

// Props for single selection calendar
type SingleCalendarProps = Omit<ComponentProps<typeof DayPicker>, 'mode'> & {
  className?: string;
  mode?: 'single';
  selected?: Date;
  onSelect?: (date: Date | undefined) => void;
};

function SingleCalendar({
  className,
  classNames,
  showOutsideDays = true,
  selected,
  ...props
}: SingleCalendarProps) {
  const [currentMonth, setCurrentMonth] = React.useState<Date | undefined>(
    selected instanceof Date ? selected : undefined
  );

  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      mode="single"
      selected={selected}
      showOutsideDays={showOutsideDays}
      month={currentMonth}
      onMonthChange={setCurrentMonth}
      className={cn('p-3', className)}
      classNames={{
        months: cn(
          'relative flex flex-col gap-4 sm:flex-row',
          defaultClassNames.months
        ),
        month: cn('flex w-full flex-col gap-4', defaultClassNames.month),
        month_caption: cn(
          'flex h-8 w-full items-center justify-center relative',
          defaultClassNames.month_caption
        ),
        caption_label: cn(
          'text-sm font-medium select-none',
          defaultClassNames.caption_label
        ),
        nav: cn(
          'absolute inset-x-0 top-0 z-10 flex w-full items-center justify-between gap-1',
          defaultClassNames.nav
        ),
        button_previous: cn(
          buttonVariants({ variant: 'outline' }),
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
          defaultClassNames.button_previous
        ),
        button_next: cn(
          buttonVariants({ variant: 'outline' }),
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
          defaultClassNames.button_next
        ),
        table: 'w-full border-collapse',
        weekdays: cn('flex', defaultClassNames.weekdays),
        weekday: cn(
          'text-muted-foreground w-8 flex-1 select-none rounded-md text-[0.8rem] font-normal',
          defaultClassNames.weekday
        ),
        week: cn('mt-2 flex w-full', defaultClassNames.week),
        day: cn(
          'group/day relative w-8 flex-1 p-0 text-center text-sm focus-within:relative focus-within:z-20',
          defaultClassNames.day
        ),
        day_button: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-8 w-8 p-0 font-normal aria-selected:opacity-100 group-data-[selected=true]/day:bg-primary group-data-[selected=true]/day:text-primary-foreground group-data-[selected=true]/day:hover:bg-primary group-data-[selected=true]/day:hover:text-primary-foreground group-data-[selected=true]/day:focus:bg-primary group-data-[selected=true]/day:focus:text-primary-foreground',
          defaultClassNames.day_button
        ),
        selected: defaultClassNames.selected,
        today: cn(
          '[&:not([data-selected=true])>button]:bg-accent [&:not([data-selected=true])>button]:text-accent-foreground',
          defaultClassNames.today
        ),
        outside: cn(
          'text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground',
          defaultClassNames.outside
        ),
        disabled: cn(
          'text-muted-foreground opacity-50',
          defaultClassNames.disabled
        ),
        hidden: cn('invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) =>
          orientation === 'left' ? (
            <ChevronLeft className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          ),
      }}
      {...props}
    />
  );
}
SingleCalendar.displayName = 'Calendar';

export { SingleCalendar };
export type { SingleCalendarProps };
